import HCaptcha from '@hcaptcha/react-hcaptcha';
import { useNotification } from '@wheely/ui-kit';
import { forwardRef, useCallback, useImperativeHandle, useRef, type RefObject } from 'react';
import { useIntl } from 'react-intl';

import { ENV } from '@/env';
import { errorHandling } from '@/services/errorHandling';
import { noop } from '@/shared/utils/noop';

type CaptchaHandle = {
  /**
   * `strict` when upstream has spent the profile's invisible budget: the check
   * stays silent, but runs on the strict sitekey, which lets fewer through than
   * the soft one used while the budget lasts.
   */
  getCaptcha: (strict?: boolean) => Promise<string | undefined>;
};

const Captcha = forwardRef<CaptchaHandle>((_, ref) => {
  const softCaptchaRef = useRef<HCaptcha>(null);
  const strictCaptchaRef = useRef<HCaptcha>(null);
  const intl = useIntl();
  const { showNotification } = useNotification();

  const runInvisibleCaptcha = useCallback(async (captchaRef: RefObject<HCaptcha>) => {
    if (!captchaRef.current) {
      errorHandling.captureMessage('Captcha reference is missing');
    }

    const result = await captchaRef.current?.execute({ async: true }).catch(error => {
      // eslint-disable-next-line no-console
      console.debug('Captcha error', error);

      errorHandling.captureMessage(
        `Captcha error: ${typeof error === 'string' ? error : 'unknown'}`,
      );
    });

    // eslint-disable-next-line no-console
    console.debug('Captcha result', result);

    // hCaptcha holds the token until the widget is reset, and upstream spends it
    // on first use. Without this a second attempt after a refusal - a throttled
    // send, a number the server would not take - would carry the token the first
    // one already burned, and fail on that instead of on the retry itself.
    captchaRef.current?.resetCaptcha();

    return result?.response;
  }, []);

  const getCaptcha = useCallback(
    async (strict = false) => {
      const token = await runInvisibleCaptcha(strict ? strictCaptchaRef : softCaptchaRef);

      if (!token) {
        showNotification({
          message: intl.formatMessage({
            defaultMessage: 'CAPTCHA verification failed. Please try again.',
            id: 'aKVZ6L',
          }),
          showCloseButton: false,
          dark: true,
        });
      }

      return token;
    },
    [runInvisibleCaptcha, intl, showNotification],
  );

  useImperativeHandle(ref, () => ({ getCaptcha }), [getCaptcha]);

  return (
    <>
      <HCaptcha
        data-test-id="captcha"
        ref={softCaptchaRef}
        onVerify={noop}
        size="invisible"
        sitekey={ENV.HCAPTCHA_INVISIBLE_SITEKEY}
      />
      <HCaptcha
        data-test-id="captcha-strict"
        ref={strictCaptchaRef}
        onVerify={noop}
        size="invisible"
        sitekey={ENV.HCAPTCHA_SITEKEY}
      />
    </>
  );
});

export { Captcha };
export type { CaptchaHandle };
